// frontend/src/pages/shop/MySettlement.jsx
import { useState, useEffect } from 'react';
import { settlementAPI } from '../../services/api';
import ShopLayout from '../../components/layout/ShopLayout';

const MySettlement = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [dateRange, setDateRange] = useState('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [filterInfo, setFilterInfo] = useState(null);

  // Format helpers
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('mn-MN').format(amount || 0) + '₮';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('mn-MN');
  };

  const getDateRangeLabel = () => {
    switch (dateRange) {
      case 'today': return 'Өнөөдөр';
      case 'yesterday': return 'Өчигдөр';
      case 'week': return 'Сүүлийн 7 хоног';
      case 'month': return 'Сүүлийн 30 хоног';
      case 'custom': return 'Сонгосон хугацаа';
      default: return 'Сүүлийн 30 хоног';
    }
  };

  // Fetch data
  const fetchSettlement = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { date_range: dateRange };
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }

      const response = await settlementAPI.getMyShopSettlement(params);
      if (response.data.success) {
        setData(response.data.data);
        setFilterInfo(response.data.data.filter);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.response?.data?.message || 'Мэдээлэл татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettlement();
  }, []);

  if (loading) {
    return (
      <ShopLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Уншиж байна...</p>
          </div>
        </div>
      </ShopLayout>
    );
  }

  if (error) {
    return (
      <ShopLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <span className="text-5xl">❌</span>
            <p className="mt-4 text-red-600 font-medium">{error}</p>
            <button 
              onClick={fetchSettlement}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Дахин оролдох
            </button>
          </div>
        </div>
      </ShopLayout>
    );
  }

  // Calculate period stats
  const periodStats = data?.period || data?.today || {};
  const balance = data?.balance || {};

  return (
    <ShopLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Төлбөр тооцоо</h1>
            <p className="text-gray-500 mt-1">Хүргэлтийн төлбөрийн мэдээлэл</p>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Хугацаа</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="today">Өнөөдөр</option>
                <option value="yesterday">Өчигдөр</option>
                <option value="week">Сүүлийн 7 хоног</option>
                <option value="month">Сүүлийн 30 хоног</option>
                <option value="custom">Хугацаа сонгох</option>
              </select>
            </div>

            {dateRange === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Эхлэх огноо</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Дуусах огноо</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </>
            )}

            <div className={`flex items-end ${dateRange === 'custom' ? '' : 'sm:col-span-1 lg:col-span-3'}`}>
              <button
                onClick={fetchSettlement}
                disabled={loading}
                className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 text-sm font-medium transition-colors"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                )}
                <span>Хайх</span>
              </button>
            </div>
          </div>

          {/* Active Filter Info */}
          {filterInfo && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md">
                  <span>📅</span>
                  <span>{getDateRangeLabel()}:</span>
                  <span className="font-medium">
                    {filterInfo.start_date === filterInfo.end_date 
                      ? formatDate(filterInfo.start_date)
                      : `${formatDate(filterInfo.start_date)} - ${formatDate(filterInfo.end_date)}`
                    }
                  </span>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1: Нийт хүргэлт */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Нийт хүргэлт</p>
                <div className="flex items-baseline gap-2 mt-2">
                  <p className="text-3xl font-bold">{periodStats.successful || periodStats.delivered || 0}</p>
                  <span className="text-blue-200 text-lg">/{periodStats.total_deliveries || periodStats.total || 0}</span>
                </div>
                <p className="text-blue-100 text-xs mt-2">{getDateRangeLabel()}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <span className="text-2xl">🚚</span>
              </div>
            </div>
          </div>

          {/* Card 2: Хугацааны төлбөр */}
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Нийт төлбөр</p>
                <p className="text-2xl font-bold mt-2">{formatCurrency(periodStats.total_fee || periodStats.fee || 0)}</p>
                <p className="text-purple-100 text-xs mt-2">{getDateRangeLabel()}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <span className="text-2xl">💵</span>
              </div>
            </div>
          </div>

          {/* Card 3: Төлсөн дүн */}
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium">Төлсөн дүн</p>
                <p className="text-2xl font-bold mt-2">{formatCurrency(balance.total_paid)}</p>
                <p className="text-emerald-100 text-xs mt-2">Нийт төлсөн</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <span className="text-2xl">✅</span>
              </div>
            </div>
          </div>

          {/* Card 4: Үлдэгдэл */}
          <div className={`bg-gradient-to-br rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all ${
            (balance.balance || 0) > 0 
              ? 'from-orange-500 to-orange-600' 
              : 'from-teal-500 to-teal-600'
          }`}>
            <div className="flex items-start justify-between">
              <div>
                <p className={`text-sm font-medium ${(balance.balance || 0) > 0 ? 'text-orange-100' : 'text-teal-100'}`}>
                  Төлөх үлдэгдэл
                </p>
                <p className="text-2xl font-bold mt-2">{formatCurrency(balance.balance)}</p>
                <p className={`text-xs mt-2 ${(balance.balance || 0) > 0 ? 'text-orange-100' : 'text-teal-100'}`}>
                  {(balance.balance || 0) > 0 ? 'Төлөгдөөгүй' : 'Тооцоо дууссан'}
                </p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                <span className="text-2xl">{(balance.balance || 0) > 0 ? '⚠️' : '🎉'}</span>
              </div>
            </div>
          </div>
        </div>

         {/* Payment Info with Bank Account */}
         {(balance.balance || 0) > 0 && (
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-xl p-4 shadow-md">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              {/* Left - Info */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">💳</span>
                </div>
                <div className="text-white">
                  <p className="font-semibold">Төлөх: {formatCurrency(balance.balance)}</p>
                         <p className="text-teal-100 text-xs">Доорх данс руу шилжүүлнэ үү</p>
                </div>
              </div>
              
              {/* Right - Bank Details */}
              {data?.shop?.company_bank_account ? (
                <div className="bg-white rounded-lg px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
                  {/* Bank & Account */}
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-lg">🏦</span>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{data.shop.company_bank_account.bank_name}</p>
                      <p className="font-mono font-bold text-gray-900">{data.shop.company_bank_account.account_number}</p>
                    </div>
                  </div>
                  
                  {/* Divider & Account Name */}
                  <div className="hidden sm:block w-px h-8 bg-gray-200"></div>
                  <div className="hidden sm:block">
                    <p className="text-xs text-gray-500">Эзэмшигч</p>
                    <p className="font-medium text-gray-900 text-sm">{data.shop.company_bank_account.account_name}</p>
                  </div>
                  
                  {/* Copy Button */}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(data.shop.company_bank_account.account_number);
                      alert('✅ Хуулагдлаа!');
                    }}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition flex items-center gap-1 self-start sm:self-center"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Хуулах
                  </button>
                </div>
              ) : (
                <div className="bg-white/20 rounded-lg px-4 py-3">
                  <p className="text-white text-sm">⚠️ Данс тохируулагдаагүй. Компанитай холбогдоно уу.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* All Paid Message */}
        {(balance.balance || 0) <= 0 && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-green-800">Төлбөр бүрэн төлөгдсөн ✓</p>
                <p className="text-sm text-green-600">Танд төлөх үлдэгдэл байхгүй байна</p>
              </div>
            </div>
          </div>
        )}

        {/* Stats Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <span>📅</span> Хүргэлтийн түүх
              </h3>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                {data?.daily_stats?.length || 0} өдөр
              </span>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Огноо</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Нийт</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Хүргэсэн</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Төлбөр</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data?.daily_stats?.length > 0 ? (
                    data.daily_stats.map((day, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-900">{formatDate(day.delivery_date)}</td>
                        <td className="px-4 py-3 text-sm text-center text-gray-600">{day.total}</td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {day.delivered}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{formatCurrency(day.fee)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-4 py-12 text-center text-gray-500">
                        <span className="text-4xl">📭</span>
                        <p className="mt-2">Хүргэлтийн түүх байхгүй</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Transaction History */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <span>💳</span> Төлбөрийн түүх
              </h3>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                {data?.transactions?.length || 0} гүйлгээ
              </span>
            </div>
            <div className="max-h-[400px] overflow-y-auto p-4 space-y-3">
              {data?.transactions?.length > 0 ? (
                data.transactions.map((tx) => (
                  <div 
                    key={tx.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        tx.transaction_type === 'payment' ? 'bg-green-100' : 'bg-orange-100'
                      }`}>
                        {tx.transaction_type === 'payment' ? (
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {tx.transaction_type === 'payment' ? 'Төлбөр төлсөн' : 'Хүргэлтийн төлбөр'}
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(tx.created_at)}</p>
                      </div>
                    </div>
                    <span className={`font-semibold ${
                      tx.amount < 0 ? 'text-green-600' : 'text-orange-600'
                    }`}>
                      {tx.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(tx.amount))}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <span className="text-4xl">📭</span>
                  <p className="mt-2">Төлбөрийн түүх байхгүй</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h3 className="font-semibold text-blue-800 flex items-center gap-2">
            <span>ℹ️</span> Мэдээлэл
          </h3>
          <ul className="mt-2 text-sm text-blue-700 space-y-1">
            <li>• Хүргэлт амжилттай болох бүрт таны хүргэлтийн төлбөр бодогдоно</li>
            {/* <li>• Нэгж хүргэлтийн төлбөр: {data?.shop?.unit_fee ? formatCurrency(data?.shop?.unit_fee) : 'Тохируулаагүй'}</li> */}
            <li>• Төлбөрийн асуудлаар хүргэлтийн компанитай холбогдоно уу</li>
          </ul>
        </div>
      </div>
    </ShopLayout>
  );
};

export default MySettlement;